/* BarConsumables.jsx - Sarf Malzemeler (sadeleştirilmiş tablo)
 * "Bara çekilen sarf malzemeler direkt tüketim sayılır" — kullanıcı isteği.
 * Sütunlar: Tarih · Sarf Malzeme Adı · Adet · Birim Fiyat (TL) · Toplam (TL)
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Search } from 'lucide-react';
import { formatTRNumber, parseLocaleNumber } from '../lib/utils';

export default function BarConsumables({ barId, date, euroRate = 35.5 }) {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [search, setSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [barId, date]);

    const loadData = async () => {
        setLoading(true);
        try {
            // B) Arama kaynağı: Ürünler kataloğu (/products) — barlardakiyle aynı veri.
            const [stockRes, catRes] = await Promise.all([
                api.get(`/consumables/${barId}`, { params: { date } }),
                api.get('/products'),
            ]);
            const its = (stockRes.data.items || []).map((it, i) => {
                const qty = parseLocaleNumber(it.consumption) || parseLocaleNumber(it.received) || 0;
                const up_tl = parseLocaleNumber(it.unit_price_tl) || 0;
                const total_tl = Math.round(qty * up_tl * 100) / 100;
                const total_eur = euroRate > 0 ? Math.round((total_tl / euroRate) * 100) / 100 : 0;
                return {
                    _rowId: `c-${i}-${Date.now()}`,
                    product_name: it.product_name || '',
                    unit: it.unit || 'Adet',
                    quantity: qty,
                    unit_price_tl: up_tl,
                    total_cost_tl: total_tl,
                    total_cost_eur: total_eur,
                    category: it.category || null,
                };
            });
            // B) "yazılan satırları silme, her zaman bir boş satır bırak"
            setItems(its.length > 0 ? [...its, emptyRow()] : [emptyRow()]);
            // products endpoint döndürür: {name, unit, category, price_tl, price_eur, ...}
            // BarConsumables `unit_price_tl` bekliyor → map et.
            const cat = (catRes.data || []).map(p => ({
                ...p,
                unit_price_tl: p.unit_price_tl ?? p.price_tl ?? 0,
            }));
            setCatalog(cat);
        } catch {
            toast.error('Sarf malzeme yüklenemedi');
            setItems([emptyRow()]);
        } finally {
            setLoading(false);
        }
    };

    const emptyRow = () => ({
        _rowId: `c-${Date.now()}-${Math.random()}`,
        product_name: '', unit: 'Adet',
        quantity: '', unit_price_tl: '',
        total_cost_tl: 0, total_cost_eur: 0,
        category: null,
    });

    const recalc = (item) => {
        const q = parseLocaleNumber(item.quantity);
        const up_tl = parseLocaleNumber(item.unit_price_tl);
        item.total_cost_tl = Math.round(q * up_tl * 100) / 100;
        item.total_cost_eur = euroRate > 0 ? Math.round((item.total_cost_tl / euroRate) * 100) / 100 : 0;
        return item;
    };

    const updateField = (idx, field, value) => {
        const next = [...items];
        next[idx] = { ...next[idx], [field]: value };
        recalc(next[idx]);
        setItems(next);
    };

    const searchCatalog = (idx, value) => {
        setActiveIndex(idx);
        const next = [...items];
        next[idx].product_name = value;
        setItems(next);
        if (!value) {
            // Boş input: tüm katalog ilk 50 öneri olarak göster
            setSuggestions(catalog.slice(0, 50));
            return;
        }
        const term = value.toLocaleLowerCase('tr');
        setSuggestions(catalog.filter(c => c.name.toLocaleLowerCase('tr').includes(term)).slice(0, 50));
    };

    const openSuggestions = (idx) => {
        setActiveIndex(idx);
        const value = (items[idx]?.product_name || '').toLocaleLowerCase('tr');
        if (!value) {
            setSuggestions(catalog.slice(0, 50));
        } else {
            setSuggestions(catalog.filter(c => c.name.toLocaleLowerCase('tr').includes(value)).slice(0, 50));
        }
    };

    const pickProduct = (idx, prod) => {
        const norm = (prod.name || '').trim().toLocaleLowerCase('tr');
        const dup = items.some((it, i) => i !== idx && (it.product_name || '').trim().toLocaleLowerCase('tr') === norm);
        if (dup) {
            toast.error(`"${prod.name}" zaten listede`);
            setSuggestions([]);
            return;
        }
        const price = Number(prod.unit_price_tl) || 0;
        const next = [...items];
        next[idx] = {
            ...next[idx],
            product_name: prod.name,
            unit: prod.unit || 'Adet',
            unit_price_tl: price,
            category: prod.category || null,
            _no_price: price <= 0,
            _not_in_catalog: false,
        };
        recalc(next[idx]);
        setItems(next);
        setSuggestions([]);
        setActiveIndex(null);
        if (price <= 0) {
            toast.warning(`⚠️ "${prod.name}" için katalogda fiyat tanımlı değil. Lütfen birim fiyatı manuel girin veya Ürünler'den ekleyin.`);
        }
    };

    const addRow = () => setItems([...items, emptyRow()]);
    const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

    const save = async () => {
        setSaving(true);
        try {
            // Backend uyumluluğu: received = adet (direkt tüketim), today_count = 0
            const validItems = items.filter(it => (it.product_name || '').trim() && parseLocaleNumber(it.quantity) > 0);
            // Fiyatsız / katalog dışı uyarısı
            const missingPriceCount = validItems.filter(it => !(parseLocaleNumber(it.unit_price_tl) > 0)).length;
            if (missingPriceCount > 0) {
                toast.warning(`⚠️ ${missingPriceCount} ürün fiyatsız kaydediliyor. Maliyet hesaplaması eksik kalabilir.`);
            }
            const payloadItems = validItems.map(it => ({
                product_name: it.product_name,
                unit: it.unit,
                yesterday_remaining: 0,
                received: parseLocaleNumber(it.quantity),
                today_count: 0,
                unit_price_tl: parseLocaleNumber(it.unit_price_tl),
                unit_price_eur: parseLocaleNumber(it.unit_price_eur),
                category: it.category || null,
            }));
            await api.post(`/consumables/${barId}`, { date, items: payloadItems });
            toast.success('Sarf malzeme kaydedildi');
            loadData();
        } catch {
            toast.error('Kaydetme başarısız');
        } finally {
            setSaving(false);
        }
    };

    const filtered = items.filter(it => {
        if (!search) return true;
        const term = search.toLocaleLowerCase('tr');
        const name = (it.product_name || '').toLocaleLowerCase('tr');
        return !name || name.includes(term);
    });

    const totalTl = items.reduce((s, it) => s + (parseLocaleNumber(it.total_cost_tl) || 0), 0);
    const totalEur = items.reduce((s, it) => s + (parseLocaleNumber(it.total_cost_eur) || 0), 0);

    // Fiyatsız ürün uyarı sayacı (kullanıcı görsün)
    const missingPriceItems = items.filter(it =>
        (it.product_name || '').trim() && !(parseLocaleNumber(it.unit_price_tl) > 0)
    );

    return (
        <div className="space-y-3" data-testid="bar-consumables">
            {missingPriceItems.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-sm" data-testid="cons-missing-price-warning">
                    <span className="text-base">⚠️</span>
                    <div>
                        <b>{missingPriceItems.length}</b> ürün katalogda fiyatsız ({missingPriceItems.slice(0, 3).map(it => it.product_name).join(', ')}{missingPriceItems.length > 3 ? '…' : ''}). Maliyet hesaplaması eksik kalır — birim fiyatı manuel girin veya Ürünler ekranından fiyat tanımlayın.
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-xs text-slate-500">
                        Bara çekilen sarf malzemeler doğrudan günün tüketimi sayılır.
                    </div>
                    <div className="text-sm text-slate-500">
                        Toplam Maliyet: <span className="font-semibold text-amber-700" data-testid="consumables-total">{formatTRNumber(totalTl, 2)} ₺</span>
                        <span className="ml-3 font-semibold text-indigo-700" data-testid="consumables-total-eur">{formatTRNumber(totalEur, 2)} €</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ara..." className="pl-8 h-9 w-40" data-testid="consumables-search" />
                    </div>
                    <Button variant="outline" size="sm" onClick={addRow} data-testid="consumables-add-row">
                        <Plus className="w-4 h-4 mr-1" /> Satır Ekle
                    </Button>
                    <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="consumables-save">
                        <Save className="w-4 h-4 mr-1" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-400 py-6 text-sm">Yükleniyor...</div>
            ) : (
                <div className="border rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-100 border-b">
                            <tr>
                                <th className="text-left p-2 font-medium text-slate-600 w-32">Tarih</th>
                                <th className="text-left p-2 font-medium text-slate-600 min-w-[260px]">Sarf Malzeme Adı</th>
                                <th className="text-center p-2 font-medium text-slate-600 w-24">Birim</th>
                                <th className="text-center p-2 font-medium text-slate-600 w-28">Adet</th>
                                <th className="text-center p-2 font-medium text-slate-600 w-32">Birim Fiyatı (TL)</th>
                                <th className="text-center p-2 font-medium text-slate-600 w-32 bg-green-50">Toplam (TL)</th>
                                <th className="text-center p-2 font-medium text-slate-600 w-32 bg-indigo-50">Toplam (€)</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="8" className="p-4 text-center text-slate-400 text-sm">Kayıt yok</td></tr>
                            ) : filtered.map((it) => {
                                const realIdx = items.indexOf(it);
                                return (
                                    <tr key={it._rowId} className="border-b hover:bg-slate-50">
                                        <td className="p-1.5 text-center text-slate-600 text-xs">{date}</td>
                                        <td className="p-1.5 relative">
                                            <Input value={it.product_name}
                                                onChange={(e) => searchCatalog(realIdx, e.target.value)}
                                                onFocus={() => openSuggestions(realIdx)}
                                                onBlur={() => { setTimeout(() => { setSuggestions([]); setActiveIndex(null); }, 200); }}
                                                className="h-9" placeholder="Tıklayıp listeden seç veya yaz ara..." data-testid={`cons-product-${realIdx}`} />
                                            {activeIndex === realIdx && suggestions.length > 0 && (
                                                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-44 overflow-auto">
                                                    {suggestions.map((s, sIdx) => (
                                                        <button key={s.id || `${s.source}-${s.name}-${sIdx}`} type="button" onMouseDown={(e) => { e.preventDefault(); pickProduct(realIdx, s); }}
                                                            className="w-full text-left px-2 py-1.5 hover:bg-slate-50 border-b last:border-0 text-sm">
                                                            <span className="font-medium">{s.name}</span>
                                                            <span className="text-xs text-slate-500 ml-2">
                                                                {s.unit} · {formatTRNumber(s.unit_price_tl, 2)}₺
                                                                {s.category ? <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{s.category}</span> : null}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-1.5">
                                            <Input value={it.unit} onChange={(e) => updateField(realIdx, 'unit', e.target.value)} className="h-9 text-center" data-testid={`cons-unit-${realIdx}`} />
                                        </td>
                                        <td className="p-1.5">
                                            <Input type="text" inputMode="decimal" value={it.quantity ?? ''}
                                                onChange={(e) => updateField(realIdx, 'quantity', e.target.value)}
                                                className="h-9 text-center" data-testid={`cons-qty-${realIdx}`} />
                                        </td>
                                        <td className="p-1.5">
                                            <Input type="text" inputMode="decimal" value={it.unit_price_tl ?? ''}
                                                onChange={(e) => updateField(realIdx, 'unit_price_tl', e.target.value)}
                                                className="h-9 text-center" data-testid={`cons-price-${realIdx}`} />
                                        </td>
                                        <td className="p-1.5 text-center bg-green-50 font-semibold text-green-700" data-testid={`cons-total-${realIdx}`}>
                                            {formatTRNumber(it.total_cost_tl || 0, 2)} ₺
                                        </td>
                                        <td className="p-1.5 text-center bg-indigo-50 font-semibold text-indigo-700" data-testid={`cons-total-eur-${realIdx}`}>
                                            {formatTRNumber(it.total_cost_eur || 0, 2)} €
                                        </td>
                                        <td className="p-1.5">
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeRow(realIdx)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
